<template>
    <AppLayout>
        <!-- Membership Header Banner -->
        <div class="membership-header">
            <div class="container">
                <div class="row align-items-center">
                    <div class="col-md-8">
                        <div class="header-content">
                            <h1 class="header-title">Membership Management</h1>
                            <p class="header-subtitle">Manage your ICDGP membership, view benefits, and track your professional journey</p>
                            <div class="membership-stats">
                                <!-- <div class="stat-item">
                                    <i class="bi bi-shield-fill-check me-2"></i>
                                    <span>{{ membership?.status || 'Inactive' }} Professional</span>
                                </div> -->
                                <div class="stat-item">
                                    <i class="bi bi-calendar-check me-2"></i>
                                    <span>Member Since {{ membership?.start_date ? new Date(membership.start_date).getFullYear() : '2025' }}</span>
                                </div>
                                <div class="stat-item">
                                    <i class="bi bi-award me-2"></i>

                                    <span>{{ membership.membership_type.charAt(0).toUpperCase() + membership.membership_type.slice(1) }} Tier</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="col-md-4 text-center">
                        <div class="membership-badge">
                            <div class="badge-circle">
                                <i class="bi bi-person-badge-fill"></i>
                            </div>
                            <h3 class="badge-title">ICDGP</h3>
                            <p class="badge-subtitle">Professional Member</p>
                        </div>
                    </div> -->
                </div>
            </div>
        </div>

        <div class="container">
            <div class="row">

                <!-- Membership Overview -->
                <div class="col-md-5">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h5 class="bold mb-0">Membership Overview</h5>
                                    <p class="subtitle">Here are your membership details and status.</p>
                                </div>

                                <!-- Show unsubscribe button when membership is active -->
                                <div class="d-none" v-if="membership?.status === 'active'">
                                    <button class="btn btn-sm btn-danger" @click="unsubscribe" :disabled="unsubscribing">
                                        <span v-if="unsubscribing">
                                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </span>
                                        <span v-else>
                                            <i class="bi bi-x-circle me-2"></i>
                                            Unsubscribe (Test)
                                        </span>
                                    </button>
                                </div>

                                <!-- Show subscribe button when membership is inactive -->
                                <div v-if="membership?.status !== 'active'">
                                    <button class="btn btn-sm btn-primary" @click="goToMembership">
                                        <i class="bi bi-star me-2"></i>
                                        Subscribe
                                    </button>
                                </div>
                            </div>

                            <ul class="list-group mt-5">
                                <li class="list-group-item">
                                    <span class="light_text">Membership Number</span>
                                    <span><b>{{ membership?.membership_number || 'Not Assigned' }}</b></span>
                                </li>
                                <li class="list-group-item">
                                    <span class="light_text">Membership Type</span>
                                    <span>{{ membership.membership_type.charAt(0).toUpperCase() + membership.membership_type.slice(1) }}</span>
                                </li>

                                <li class="list-group-item">
                                    <span class="light_text">Membership Status</span>
                                    <div class="badge px-3" :class="membership?.status === 'active' ? 'bg-success' : (membership?.status || 'bg-secondary')">
                                        {{ membership?.status || 'Inactive' }}
                                    </div>
                                </li>

                                <li class="list-group-item">
                                    <span class="light_text">Start Date</span>
                                    <span>{{ formatDate(membership?.start_date) }}</span>
                                </li>

                                <li class="list-group-item">
                                    <span class="light_text">Expiry Date</span>
                                    <span>{{ formatDate(membership?.expiry_date) }}</span>
                                </li>

                                <li class="list-group-item">
                                    <span class="light_text">Renewal Count</span>
                                    <span>{{ membership?.renewal_count || 0 }} times</span>
                                </li>

                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Payment History -->
                <div class="col-md-7" id="payment-history">
                    <div class="card h-100">
                        <div class="card-body" style="max-height: 350px; overflow-y: auto;">
                            <h5 class="bold mb-0">Payment History</h5>
                            <p class="subtitle">Below are your recent membership payments.</p>

                            <table class="table table-striped table-hover mt-4">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Reference</th>
                                        <th>Amount</th>
                                        <th>Gateway</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="payment in payments" :key="payment.id">
                                        <td>{{ formatDate(payment.payment_date) }}</td>
                                        <td><small class="text-muted">{{ payment.payment_reference }}</small></td>
                                        <td><strong>{{ payment.currency }} {{ payment.amount }}</strong></td>
                                        <td>
                                            {{ payment.payment_gateway }}
                                        </td>
                                        <td>
                                            <span class="badge" :class="{
                                                'bg-success': payment.status === 'completed',
                                                'bg-warning': payment.status === 'pending',
                                                'bg-danger': payment.status === 'failed'
                                            }">
                                                {{ payment.status }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="payments.length === 0">
                                        <td colspan="5" class="text-center text-muted">No payment history available</td>
                                    </tr>
                                </tbody>
                            </table>


                        </div>
                    </div>
                </div>


            </div>

            <!-- Membership Tiers & Benefits -->
            <div class="row mt-4">
                <div class="col-md-8">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h5 class="bold mb-0">Membership Tiers & Benefits</h5>
                                    <p class="subtitle">Compare membership levels and their benefits.</p>
                                </div>

                                <!-- Only show the button if im not on corporate  -->
                                <!-- <template v-if="membership?.membership_type !== 'corporate'">
                                    <button class="btn btn-sm btn-primary mb-3">Upgrade Membership</button>
                                </template> -->
                            </div>




                            <div class="table-responsive mt-4">
                                <table class="table table-bordered">
                                    <thead class="table-light">
                                        <tr>
                                            <th>Feature</th>
                                            <th>Student <span v-if="membership?.membership_type === 'student'" class="badge bg-primary ms-1">Current</span></th>
                                            <th>Individual <span v-if="membership?.membership_type === 'individual'" class="badge bg-primary ms-1">Current</span></th>
                                            <th>Corporate <span v-if="membership?.membership_type === 'corporate'" class="badge bg-primary ms-1">Current</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Course Access</strong></td>
                                            <td><i class="bi bi-check-circle text-success"></i> Basic</td>
                                            <td><i class="bi bi-check-circle text-success"></i> Full Access</td>
                                            <td><i class="bi bi-check-circle text-success"></i> Full + Premium</td>
                                        </tr>
                                        <tr>
                                            <td><strong>CPD Hours</strong></td>
                                            <td>20 hours/year</td>
                                            <td>40 hours/year</td>
                                            <td>Unlimited</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Mentorship</strong></td>
                                            <td><i class="bi bi-x-circle text-danger"></i></td>
                                            <td><i class="bi bi-check-circle text-success"></i> Group Sessions</td>
                                            <td><i class="bi bi-check-circle text-success"></i> 1-on-1 + Group</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Job Board Access</strong></td>
                                            <td><i class="bi bi-check-circle text-success"></i></td>
                                            <td><i class="bi bi-check-circle text-success"></i></td>
                                            <td><i class="bi bi-check-circle text-success"></i> Priority Listings</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Annual Fee</strong></td>
                                            <td>{{ tierDetails.student ? tierDetails.student.currency_symbol + tierDetails.student.price : 'N/A' }}</td>
                                            <td>{{ tierDetails.individual ? tierDetails.individual.currency_symbol + tierDetails.individual.price : 'N/A' }}</td>
                                            <td>{{ tierDetails.corporate ? tierDetails.corporate.currency_symbol + tierDetails.corporate.price : 'N/A' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Actions -->
                <div class="col-md-4">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="bold mb-0">Renewal Reminders</h5>
                            <p class="subtitle">Manage your notification preferences.</p>

                            <div class="mt-4">
                                <div class="form-check mb-3">
                                    <input class="form-check-input" type="checkbox" id="email-reminders" v-model="emailReminders">
                                    <label class="form-check-label" for="email-reminders">
                                        Email Reminders
                                        <small class="text-muted d-block">Get renewal reminders via email</small>
                                    </label>
                                </div>

                                <div class="form-check mb-3">
                                    <input class="form-check-input" type="checkbox" id="sms-reminders" v-model="smsReminders">
                                    <label class="form-check-label" for="sms-reminders">
                                        SMS Notifications
                                        <small class="text-muted d-block">Receive text message alerts</small>
                                    </label>
                                </div>

                                <div class="mb-3">
                                    <label for="reminder-frequency" class="form-label">Subscription Expiry Reminder</label>
                                    <select class="form-select" id="reminder-frequency" v-model="expiryReminder">
                                        <option :value="30">30 days before expiry</option>
                                        <option :value="60">60 days before expiry</option>
                                        <option :value="90">90 days before expiry</option>
                                    </select>
                                </div>

                                <!-- Error messages -->
                                <div v-if="Object.keys(reminderErrors).length > 0" class="alert alert-danger mb-3">
                                    <ul class="mb-0">
                                        <li v-for="(error, field) in reminderErrors" :key="field">
                                            {{ error[0] }}
                                        </li>
                                    </ul>
                                </div>

                                <button class="btn btn-sm btn-outline-primary" @click="saveReminderPreferences" :disabled="savingReminders">
                                    <span v-if="savingReminders">
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </span>
                                    <span v-else>
                                        Save Preferences
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


        </div>


        <!-- Membership Subscription Modal -->
        <div v-if="showMembershipModal" class="membership-modal-overlay" @click="closeMembershipModal">
            <div class="membership-modal" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">Choose Your Membership Tier</h3>
                    <button type="button" class="btn-close" @click="closeMembershipModal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <p class="modal-subtitle">Select the membership tier that best fits your professional needs and career goals.</p>

                    <div class="membership-tiers">
                        <!-- Student Tier -->
                        <div class="tier-card">
                            <div class="tier-header">
                                <h4 class="tier-name">Student</h4>
                                <div class="tier-price">
                                    <span class="price">{{ tierPrices.student ? 'R' + tierPrices.student : 'N/A' }}</span>
                                    <span class="period">/year</span>
                                </div>
                            </div>
                            <div class="tier-features">
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Basic Course Access</span>
                                </div>
                                <div class="feature-item">
                                    <span class="fw-bold">20 CPD Hours/year</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-x-circle text-danger me-2"></i>
                                    <span>No Mentorship</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Job Board Access</span>
                                </div>
                            </div>
                            <button class="btn btn-primary w-100" @click="subscribeToTier('student')">
                                Subscribe to Student
                            </button>
                        </div>

                        <!-- Individual Tier -->
                        <div class="tier-card featured">
                            <div class="tier-badge">Most Popular</div>
                            <div class="tier-header">
                                <h4 class="tier-name">Individual</h4>
                                <div class="tier-price">
                                    <span class="price">{{ tierPrices.individual ? 'R' + tierPrices.individual : 'N/A' }}</span>
                                    <span class="period">/year</span>
                                </div>
                            </div>
                            <div class="tier-features">
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Full Course Access</span>
                                </div>
                                <div class="feature-item">
                                    <span class="fw-bold">40 CPD Hours/year</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Group Mentorship Sessions</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Job Board Access</span>
                                </div>
                            </div>
                            <button class="btn btn-primary w-100" @click="subscribeToTier('individual')">
                                Subscribe to Individual
                            </button>
                        </div>

                        <!-- Corporate Tier -->
                        <div class="tier-card">
                            <div class="tier-header">
                                <h4 class="tier-name">Corporate</h4>
                                <div class="tier-price">
                                    <span class="price">{{ tierPrices.corporate ? 'R' + tierPrices.corporate : 'N/A' }}</span>
                                    <span class="period">/year</span>
                                </div>
                            </div>
                            <div class="tier-features">
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Full + Premium Course Access</span>
                                </div>
                                <div class="feature-item">
                                    <span class="fw-bold">Unlimited CPD Hours</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>1-on-1 & Group Mentorship</span>
                                </div>
                                <div class="feature-item">
                                    <i class="bi bi-check-circle text-success me-2"></i>
                                    <span>Priority Job Board Listings</span>
                                </div>
                            </div>
                            <button class="btn btn-primary w-100" @click="subscribeToTier('corporate')">
                                Subscribe to Corporate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import AppLayout from '../Layouts/AppLayout.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';

const props = defineProps({
    user: Object,
    membership: Object,
    payments: Array,
    membershipTypes: Array,
});

// Reactive state for modal
const showMembershipModal = ref(false);
const unsubscribing = ref(false);

// Reminder preferences
const emailReminders = ref(true);
const smsReminders = ref(false);
const expiryReminder = ref(30);
const savingReminders = ref(false);
const reminderErrors = ref({});

// Computed property to get tier details
const tierDetails = computed(() => {
    const details = {};
    if (props.membershipTypes) {
        props.membershipTypes.forEach(tier => {
            details[tier.name.toLowerCase()] = {
                price: tier.price,
                currency_symbol: tier.currency_symbol,
            };
        });
    }
    return details;
});

// Handle escape key to close modal
const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && showMembershipModal.value) {
        closeMembershipModal();
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleEscapeKey);

    // Initialize reminder preferences from user data
    if (props.user) {
        // Always coerce to boolean for checkboxes
        emailReminders.value = props.user.email_reminders === 'true';
        smsReminders.value = props.user.sms_reminders === 'true';
        expiryReminder.value = props.user.expiry_reminder ?? 30;
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscapeKey);
});

// Helper function to format dates
const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Function to show membership modal
const goToMembership = () => {
    showMembershipModal.value = true;
};

// Function to close membership modal
const closeMembershipModal = () => {
    showMembershipModal.value = false;
};

// Function to handle subscription
const subscribeToTier = (tier) => {
    router.post('/membership/subscribe', { tier });
    closeMembershipModal();
};

// Function to handle unsubscribe (expire membership for testing)
const unsubscribe = () => {
    unsubscribing.value = true;
    router.post('/membership/unsubscribe', {}, {
        onSuccess: () => {
            unsubscribing.value = false;
        },
        onError: (errors) => {
            unsubscribing.value = false;
            console.error('Unsubscribe error:', errors);
        }
    });
};

// Function to save reminder preferences
const saveReminderPreferences = () => {
    savingReminders.value = true;
    reminderErrors.value = {};

    router.patch('/membership/reminders', {
        email_reminders: emailReminders.value ? 'true' : 'false',
        sms_reminders: smsReminders.value ? 'true' : 'false',
        expiry_reminder: expiryReminder.value,
    }, {
        onSuccess: () => {
            savingReminders.value = false;
        },
        onError: (errors) => {
            savingReminders.value = false;
            if (errors) {
                reminderErrors.value = errors;
            }
            console.error('Save reminders error:', errors);
        }
    });
};
</script>


<style scoped>
/* Membership Header Banner */
.membership-header {
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    color: white;
    padding: 4rem 0;
    margin-bottom: 3rem;
    position: relative;
    overflow: hidden;
}

.membership-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
}

.header-content {
    position: relative;
    z-index: 2;
}

.header-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;
}

.header-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin-bottom: 2rem;
    line-height: 1.6;
}

.membership-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
}

.stat-item {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 500;
}

.stat-item i {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
}

.membership-badge {
    position: relative;
    z-index: 2;
}

.badge-circle {
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    font-size: 3rem;
    color: white;
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 255, 255, 0.3);
}

.badge-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.badge-subtitle {
    font-size: 1rem;
    opacity: 0.8;
    margin-bottom: 0;
}

/* Bootstrap Primary Color Override */
:deep(.btn-primary) {
    background-color: #1e9579;
    border-color: #1e9579;
}

:deep(.btn-primary:hover) {
    background-color: #176f5e;
    border-color: #176f5e;
}

:deep(.text-primary) {
    color: #1e9579 !important;
}

:deep(.bg-primary) {
    background-color: #1e9579 !important;
}

/* Card Enhancement */
.card {
    border: 1px solid #e9ecef;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
}

.card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

/* Responsive Design for Header */
@media (max-width: 768px) {
    .membership-header {
        padding: 3rem 0;
        text-align: center;
    }

    .header-title {
        font-size: 2rem;
    }

    .header-subtitle {
        font-size: 1.1rem;
    }

    .membership-stats {
        justify-content: center;
        gap: 1.5rem;
    }

    .badge-circle {
        width: 80px;
        height: 80px;
        font-size: 2.5rem;
    }

    .badge-title {
        font-size: 1.5rem;
    }
}

td,
th {
    border: none;
}

/* Membership tier comparison table */
.table-bordered th,
.table-bordered td {
    border: 1px solid #dee2e6;
    vertical-align: middle;
}

.table-light {
    background-color: #f8f9fa;
}

/* Quick action buttons */
.btn {
    font-weight: 500;
    border-radius: 8px;
}

/* List group improvements */
.list-group-flush .list-group-item {
    border-left: none;
    border-right: none;
    padding: 0.75rem 0;
}

.light_text {
    color: #6c757d;
    font-weight: 400;
}

.bold {
    font-weight: 600;
}

.subtitle {
    color: #6c757d;
    font-size: 0.9rem;
    margin-bottom: 0;
}

/* Card hover effects */
.card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    transition: box-shadow 0.15s ease-in-out;
}

.card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Form controls */
.form-check-label small {
    font-size: 0.8rem;
    margin-top: 0.25rem;
}

.form-select,
.form-check-input {
    border-radius: 6px;
}

/* Icon styling */
.bi {
    font-size: 1rem;
}

/* Badge styling */
.badge {
    font-size: 0.75rem;
    font-weight: 500;
}

/* Membership Modal Styles */
.membership-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 1rem;
}

.membership-modal {
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
}

.btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.btn-close:hover {
    background: #f8f9fa;
    color: #495057;
}

.modal-body {
    padding: 2rem;
}

.modal-subtitle {
    color: #6c757d;
    font-size: 1rem;
    margin-bottom: 2rem;
    text-align: center;
}

.membership-tiers {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
}

.tier-card {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 2rem 1.5rem;
    text-align: center;
    position: relative;
    transition: all 0.3s ease;
}

.tier-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.tier-card.featured {
    border-color: #1e9579;
    box-shadow: 0 4px 15px rgba(30, 149, 121, 0.2);
}

.tier-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e9579;
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
}

.tier-header {
    margin-bottom: 1.5rem;
}

.tier-name {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.5rem;
}

.tier-price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
}

.price {
    font-size: 2rem;
    font-weight: 700;
    color: #1e9579;
}

.period {
    color: #6c757d;
    font-size: 1rem;
}

.tier-features {
    margin-bottom: 2rem;
    text-align: left;
}

.feature-item {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    color: #495057;
}

.feature-item i {
    flex-shrink: 0;
}

.tier-card .btn-primary {
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border: none;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.tier-card .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(30, 149, 121, 0.3);
}

/* Responsive Design for Modal */
@media (max-width: 768px) {
    .membership-modal {
        margin: 1rem;
        max-height: calc(100vh - 2rem);
    }

    .modal-header {
        padding: 1rem 1.5rem;
    }

    .modal-body {
        padding: 1.5rem;
    }

    .membership-tiers {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .tier-card {
        padding: 1.5rem 1rem;
    }

    .modal-title {
        font-size: 1.25rem;
    }
}

/* Responsive Design */
@media (max-width: 576px) {
    .membership-modal-overlay {
        padding: 0.5rem;
    }

    .tier-card {
        padding: 1.25rem 1rem;
    }

    .tier-name {
        font-size: 1.25rem;
    }

    .price {
        font-size: 1.75rem;
    }
}
</style>
